/**
 * Idempotency-key helper for `httpsCallable` payloads.
 *
 * Server side (`functions/main.py`) caches the result of each
 * `(opName, requestId)` pair for 24 h, so a retry — Firebase internal
 * retry, user double-submit, or a manual retry after a network blip —
 * replays the cached response instead of re-executing side effects
 * (double Stripe charges, duplicate emails, etc.).
 *
 * Usage at every side-effecting call site:
 *
 *   const fn = httpsCallable<MyInput & { requestId: string }, MyOutput>(
 *     functions,
 *     'my_cf',
 *   )
 *   await fn(withRequestId({ clubId, ...rest }))
 *
 * One UUID per `.mutate()` call is the right grain: TanStack Query
 * mutations don't auto-retry, so a fresh id per click is fine. If the
 * caller wants stable retries (same intent, multiple network attempts)
 * they should hoist the id into a ref and pass it explicitly via the
 * second argument override.
 */
export function withRequestId<T extends object>(
  payload: T,
  override?: string,
): T & { requestId: string } {
  return {
    ...payload,
    requestId: override ?? crypto.randomUUID(),
  }
}
